import liteThresholdSearchQuery from '@api/graphql/liteThresholdSearch';
import axios from '@axios';
import type { Experiments } from '@typings/redux';
import type { StateCodes } from '@typings/states';
import { generateSeed } from '@utils/generateSeed';
import { processSortAndVariant } from '@utils/sortParam';
import {
	type RangeFilter,
	categoryMap,
	rangeFiltersMap,
	vanityGuids,
} from './utils';

type SearchType = 'distance' | 'featured' | 'recommended' | 'review-rating';

type ExperimentHeaders = { [key: string]: string };

export interface SearchArgs {
	categoryGuid?: string;
	city: string;
	filters?: string[];
	fixedPageSize?: boolean;
	limit: number;
	page: number;
	seed: string;
	rangeFilters?: RangeFilter[];
	sort?: SearchType;
	state: StateCodes;
	visitorId?: string;
	experiments?: Experiments;
	member?: {
		anonymousId?: string;
		userId?: string;
	};
	shouldShowPricingFilter?: boolean;
	searchString?: string;
	shouldFetchPriceRangeIntervals?: boolean;
	skipCrossCategoryClassification?: boolean;
}

const search = async (
	args: SearchArgs,
	experimentHeaders?: ExperimentHeaders,
): Promise<API.SearchResponse> => {
	const {
		categoryGuid,
		city,
		filters,
		fixedPageSize,
		limit,
		page,
		rangeFilters,
		seed,
		sort,
		state,
		member,
		shouldShowPricingFilter,
		searchString,
		shouldFetchPriceRangeIntervals = false,
		skipCrossCategoryClassification,
	} = args;
	const { anonymousId, userId } = member || {};

	const shouldOnlyShowBaseFilter =
		shouldShowPricingFilter &&
		!!categoryGuid &&
		filters?.some((filter) => vanityGuids.includes(filter));
	const rangeFiltersFromFacet: RangeFilter[] = [];

	if (shouldShowPricingFilter && categoryGuid) {
		const vanityMatch = filters?.find((filter) => vanityGuids.includes(filter));
		const catCode = categoryMap[categoryGuid];
		const rangeFilterKey = `${catCode}-${vanityMatch}`;
		const rangeFilter = rangeFiltersMap[rangeFilterKey];

		if (rangeFilter) {
			rangeFiltersFromFacet.push(rangeFilter);
		}
	}
	const includeCategory = !!categoryGuid;
	const searchFilters = !includeCategory
		? filters || []
		: shouldOnlyShowBaseFilter
			? [categoryGuid]
			: filters?.length
				? filters
				: [categoryGuid];
	const apiPage = page > 0 ? page - 1 : 0;
	const data = {
		query: liteThresholdSearchQuery(
			shouldFetchPriceRangeIntervals,
			includeCategory,
		),
		variables: {
			...(includeCategory ? { categoryGuid } : {}),
			city,
			filters: searchFilters,
			limit: limit || 30,
			page: apiPage,
			rangeFilters: rangeFiltersFromFacet.length
				? rangeFiltersFromFacet
				: rangeFilters,
			search_fallback_offset: apiPage * 30,
			seed: seed || generateSeed(),
			sort: processSortAndVariant(sort, searchString),
			state,
			fixedPageSize: fixedPageSize || false,
			member: { anonymousId, userId },
			...(searchString ? { searchString } : {}),
			...(skipCrossCategoryClassification
				? { skipCrossCategoryClassification: true }
				: {}),
		},
	};

	const response = await axios.post<API.SearchResponseData>(data, {
		headers: experimentHeaders,
	});

	return response;
};

export default search;
